// date.utils.js

function valid(value) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.getTime();
  } catch (err) {
    return undefined;
  }
}

function toDate(value) {
  if (value == null) return value; // preserve null/undefined
  return typeof value === "string" || typeof value === "number" ? new Date(value) : value;
}

function parseDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

function formatDateOnly(value) {
  const date = parseDateOnly(value);
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function shortMonthYear(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

function longDay(date, locale = "en-US") {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(d);
}

function longMonth(date, locale = "en-US") {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { month: "long" }).format(d);
}

function format(date, includeTime = false, timeOnly = false) {
  try {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return "";

    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");

    if (timeOnly) return `${hh}:${mm}`;

    const mo = d.getMonth() + 1;
    const dd = d.getDate();
    const yyyy = d.getFullYear();

    // If you want "01.23.2026 14:05", this adds the space.
    return `${mo}.${dd}.${yyyy}${includeTime ? ` ${hh}:${mm}` : ""}`;
  } catch (error) {
    return "";
  }
}

function formatMinutesToHours(minutes) {
  if (minutes == null) return null;

  const total = Math.max(0, Math.trunc(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;

  return `${h}:${String(m).padStart(2, "0")} hr`;
}

function minutesToHM(minutes) {
  const total = Math.max(0, Math.trunc(Number(minutes) || 0));
  return { h: Math.floor(total / 60), m: total % 60 };
}

function formatMinutesToHM(minutes){
  if (minutes == null) return null;
  const {h, m} = minutesToHM(minutes);
  return `${h}:${String(m).padStart(2, "0")}`;
}
    
function dateToMD(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  // Pull parts in America/New_York and assemble without dayPeriod (am/pm)
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);

  const m = parts.find(p => p.type === "month")?.value;
  const day = parts.find(p => p.type === "day")?.value;

  if (!m || !day) return null;
  return `${m}/${day}`;
}

function setFormattedHours(dateValue, hhmm) {
  if (!dateValue || !hhmm) return null;

  const d = dateValue instanceof Date ? new Date(dateValue.getTime()) : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;

  const [hhRaw, mmRaw] = String(hhmm).split(":");
  const hh = parseInt(hhRaw, 10);
  const mm = parseInt(mmRaw, 10);

  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

  // local time
  d.setHours(hh, mm, 0, 0);
  return d; // call .toISOString() if backend wants ISO
}

function formatShortDate(date) {
  if (!date) return null;

  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(d);
}

function formatTime({
  date,
  timeZone = "America/New_York",
  hour12 = true,
  minute = "2-digit",
  hour = "numeric",
} = {}) {
  if (!date) return null;

  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  return new Intl.DateTimeFormat("en-US", {
    hour,
    minute,
    hour12,
    timeZone,
  }).format(d);
}

function minutesBetween(date1, date2) {
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;

  const diff = (d2.getTime() - d1.getTime()) / 1000 / 60;
  return Math.abs(Math.round(diff));
}

function daysBetween(date1, date2) {
  const mins = minutesBetween(date1, date2);
  if (mins == null) return null;
  return Math.floor(mins / 60 / 24);
}

function addMinutes(date, number) {
  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  d.setMinutes(d.getMinutes() + (Number(number) || 0));
  return d;
}

function addDays(date, number) {
  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + (Number(number) || 0));
  return d;
}

function compare(date1, date2, reverse = false) {
  if (date1 < date2) return reverse ? 1 : -1;
  if (date1 > date2) return reverse ? -1 : 1;
  return 0;
}

function dayOfYear(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  const startOfYear = new Date(d.getFullYear(), 0, 0);
  const diff = d - startOfYear;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}

function dayOfMonth(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.getDate(); // 1..31
}

function yesterday() {
  const today = new Date();
  return new Date(today.getTime() - 24 * 60 * 60 * 1000);
}

function greatest(arr) {
  const maxTs = Math.max(
    ...((arr || [])
      .filter(Boolean)
      .map((x) => (x instanceof Date ? x.getTime() : new Date(x).getTime()))
      .filter(Number.isFinite))
  );
  return Number.isFinite(maxTs) ? new Date(maxTs) : null;
}

function least(arr) {
  const minTs = Math.min(
    ...((arr || [])
      .filter(Boolean)
      .map((x) => (x instanceof Date ? x.getTime() : new Date(x).getTime()))
      .filter(Number.isFinite))
  );
  return Number.isFinite(minTs) ? new Date(minTs) : null;
}

const pad2 = (n) => String(n).padStart(2, "0");

function asICSLocalDT(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";

  const y = dt.getFullYear();
  const m = pad2(dt.getMonth() + 1);
  const day = pad2(dt.getDate());
  const h = pad2(dt.getHours());
  const min = pad2(dt.getMinutes());
  const s = pad2(dt.getSeconds());

  return `${y}${m}${day}T${h}${min}${s}`;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

module.exports = {
  valid,
  toDate,
  parseDateOnly,
  formatDateOnly,
  shortMonthYear,
  longDay,
  longMonth,
  format,
  formatMinutesToHours,
  minutesToHM,
  setFormattedHours,
  formatShortDate,
  formatTime,
  minutesBetween,
  daysBetween,
  addMinutes,
  addDays,
  compare,
  dayOfYear,
  dayOfMonth,
  yesterday,
  greatest,
  least,
  asICSLocalDT,
  sameDay,
  dateToMD,
  formatMinutesToHM
};
